import { type ExportParticipantRequest, JobState } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { FaDownload } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import { useAppContext } from '../../hooks/useAppContext';
import { useParticipantExportJob } from '../../hooks/useJob';
import { usePermission } from '../../hooks/usePermission';
import { ParticipantExportModal } from './ParticipantExportModal';

interface Props {
  participantId: string;
}

export const ParticipantExportMenuItem = (props: Props) => {
  const { participantId } = props;
  const { organization, promiseToast } = useAppContext();

  const hasPermission = usePermission('read', 'participant');

  const exportJob = useParticipantExportJob();

  const onExport = React.useCallback(
    async (request: ExportParticipantRequest) => {
      const promise = async (): Promise<string> => {
        const job = await exportJob.start({
          organizationId: organization.id,
          participantId: participantId,
          request,
        });

        if (job?.state !== JobState.Completed) {
          throw new Error('Participant export was not generated');
        }

        return job.response.url;
      };

      await promiseToast(promise, {
        loading: {
          title: 'Generating participant export...',
        },
        success: (url) => ({
          title: 'Participant export successfully generated',
          description: 'Your archive is ready. Download it before closing this message.',
          duration: Number.POSITIVE_INFINITY,
          closable: true,
          action: {
            label: 'Download',
            onClick: () => window.open(url, '_self'),
          },
        }),
        error: {
          title: 'Error exporting participant',
        },
      });
    },
    [exportJob, organization.id, participantId, promiseToast],
  );

  return (
    <ChakraV3.MenuItem
      onClick={() => showModal(ParticipantExportModal, { onExport })}
      disabled={!hasPermission}
      value='export-participant'
    >
      <FaDownload />
      Export participant (ZIP)
    </ChakraV3.MenuItem>
  );
};
